import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { DataTable2 } from '../components/ui/DataTable2';
import { Loader2, Info, XCircle, FileText, CalendarDays, DollarSign, ArrowLeftRight, ChevronLeft, User, RefreshCcw } from 'lucide-react';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { fetchAccountDetails } from '../apiHelper';

const client = new FastAPIClient();

/**
 * AccountDetailsPage Component.
 * Displays detailed transaction postings for a specific accounting account as a standalone page.
 * Receives accountId and accountName via location state.
 */
const AccountDetailsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();

    const accountId = location.state?.accountId;
    const accountName = location.state?.accountName || 'Account Details';
    const user = location.state?.user;

    const [postings, setPostings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30); // Default to last 30 days
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    // Effect to load user from local storage if not passed via state
    useEffect(() => {
        if (!user) {
            const lcUser = localStorage.getItem('user');
            if (!lcUser) {
                showToast('Please log in to view account details.', 'info');
                navigate('/login');
            } else {
                setLoggedInUser(JSON.parse(lcUser));
            }
        } else {
            setLoggedInUser(user);
        }
    }, [user, showToast, navigate]);

    // Fetch account details (postings) when component mounts or accountId/loggedInUser/startDate/endDate changes
    const fetchAccountData = useMemo(() => {
        if (!accountId || !loggedInUser) return null;

        return async () => {
            setLoading(true);
            setError(null);
            try {
                const params = {
                    account_id: accountId,
                    start_date: startDate,
                    end_date: endDate
                };
                const fetchedData = await fetchAccountDetails(params, loggedInUser);
                if (fetchedData && Array.isArray(fetchedData.postings)) {
                    setPostings(fetchedData.postings);
                } else {
                    setPostings([]);
                    showToast('Unexpected data structure received for account postings.', 'error');
                }
            } catch (err) {
                console.error(`Error loading postings for account ${accountId}:`, err.response);
                const errorMessage = err.response?.data?.detail || `Failed to load postings for ${accountName}.`;
                setError(errorMessage);
                showToast(errorMessage, 'error');
            } finally {
                setLoading(false);
            }
        };
    }, [accountId, accountName, loggedInUser, startDate, endDate, showToast]);

    useEffect(() => {
        if (fetchAccountData) {
            fetchAccountData();
        }
    }, [fetchAccountData]);

    // Handle date change
    const handleDateChange = (setter) => (e) => {
        setter(e.target.value);
    };

    // Define columns for the postings table
    const postingsColumns = useMemo(() => [
        {
            key: 'transaction.transaction_date',
            label: 'Date',
            searchable: (row) => {
                try {
                    return new Date(row.transaction.transaction_date).toLocaleDateString();
                } catch {
                    return '';
                }
            },
            render: (value, row) => {
                try {
                    return new Date(row.transaction.transaction_date).toLocaleDateString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                    });
                } catch {
                    return 'Invalid Date';
                }
            }
        },
        {
            key: 'transaction.ref',
            label: 'Reference',
            searchable: (row) => row.transaction?.ref,
            render: (value, row) => (
                <div className="flex items-center gap-1">
                    <FileText size={14} className="text-gray-500" />
                    {row.transaction?.orders_id ? (
                        <span
                            onClick={() => navigate('/sales/order-details', {
                                state: { ordersId: row.transaction.orders_id, user: loggedInUser }
                            })}
                            className="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        >
                            {row.transaction.ref || 'N/A'}
                        </span>
                    ) : (
                        <span className="font-mono text-xs">{row.transaction.ref || 'N/A'}</span>
                    )}
                </div>
            )
        },
        {
            key: 'transaction.description',
            label: 'Description',
            searchable: (row) => row.transaction?.description,
            render: (value, row) => (
                <div className="whitespace-normal break-words max-w-xs">
                    {row.transaction.description || 'N/A'}
                </div>
            )
        },
        {
            key: 'posting_type',
            label: 'Type',
            render: (posting_type) => (
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${posting_type === 'debit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {posting_type.toUpperCase()}
                </span>
            )
        },
        {
            key: 'amount',
            label: 'Amount',
            render: (amount, row) => {
                const currencyCode = row.transaction?.currency_code || '₦';
                return `${currencyCode}${parseFloat(amount).toFixed(2)}`;
            }
        },
        {
            key: 'creator',
            label: 'Creator',
            searchable: (row) => `${row.transaction?.creator?.user?.first_name || ''} ${row.transaction?.creator?.user?.last_name || ''}`.trim(),
            render: (value, row) => {
                const creator = row.transaction?.creator?.user;
                return creator ? (
                    <div className="flex items-center gap-1">
                        <User size={14} className="text-gray-500" />
                        <span>{`${creator.first_name || ''} ${creator.last_name || ''}`.trim()}</span>
                    </div>
                ) : 'N/A';
            }
        },
        {
            key: 'customer',
            label: 'Customer',
            searchable: (row) => `${row.transaction?.customer?.user?.first_name || ''} ${row.transaction?.customer?.user?.last_name || ''}`.trim(),
            render: (value, row) => {
                const customer = row.transaction?.customer?.user;
                return customer ? (
                    <div className="flex items-center gap-1">
                        <User size={14} className="text-gray-500" />
                        <span>{`${customer.first_name || ''} ${customer.last_name || ''}`.trim()}</span>
                    </div>
                ) : 'N/A';
            }
        },
    ], [navigate, loggedInUser]);

    if (!loggedInUser) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to view account details.</p>
            </div>
        );
    }

    if (!accountId) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-red-600'>
                <XCircle size={32} className="mb-2" />
                <p className="text-lg mb-4">No account selected. Please go back to Accounting Dashboard.</p>
                <Button onClick={() => navigate('/accounting')} variant="primary" className="mt-4">
                    <ChevronLeft size={20} className="mr-2" /> Go to Accounting
                </Button>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <Card className="p-4 sm:p-6 shadow-lg rounded-lg">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-text-primary flex items-center gap-2">
                        <ArrowLeftRight size={24} className="text-blue-600" /> Transaction Details for: {accountName}
                    </h2>
                    <Button className="flex items-center justify-center px-4 py-3" onClick={() => navigate('/accounting')} variant="secondary" size="sm">
                        <ChevronLeft size={20} className="mr-2" /> Back to Accounting
                    </Button>
                </div>
                <p className="text-text-secondary mb-6">
                    Individual transaction postings for this account from {startDate} to {endDate}.
                </p>
                {/* Filters and Controls */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                        <div className="flex items-center gap-2 w-full">
                            <label htmlFor="startDate" className="text-sm font-medium text-gray-700">From:</label>
                            <div className="relative w-full">
                                <input
                                    type="date"
                                    id="startDate"
                                    value={startDate}
                                    onChange={handleDateChange(setStartDate)}
                                    className="block w-full rounded-md border-gray-300 shadow-sm pl-9 pr-3 py-2 text-gray-900 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <CalendarDays className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 w-full">
                            <label htmlFor="endDate" className="text-sm font-medium text-gray-700">To:</label>
                            <div className="relative w-full">
                                <input
                                    type="date"
                                    id="endDate"
                                    value={endDate}
                                    onChange={handleDateChange(setEndDate)}
                                    className="block w-full rounded-md border-gray-300 shadow-sm pl-9 pr-3 py-2 text-gray-900 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <CalendarDays className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <Button onClick={fetchAccountData} disabled={loading} className="flex items-center justify-center w-full sm:w-auto">
                        {loading ? <Loader2 className="animate-spin" size={20} /> : <RefreshCcw size={20} />}
                        <span className="ml-2">Refresh Details</span>
                    </Button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="animate-spin h-6 w-6 mr-2 text-blue-500" />
                        <p className="text-gray-600">Loading account postings...</p>
                    </div>
                ) : error ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                        <p>{error}</p>
                    </div>
                ) : (
                    <DataTable2
                        columns={postingsColumns}
                        data={postings}
                        loading={loading}
                        error={error}
                        emptyMessage={`No transaction postings found for ${accountName}.`}
                        enableSearch={true}
                        enablePagination={true}
                        rowsPerPage={10}
                    />
                )}
            </Card>
        </div>
    );
};

export default AccountDetailsPage;